import Vue from 'vue';
import {extend} from 'vee-validate';
import * as rules from 'vee-validate/dist/rules';
import {messages} from 'vee-validate/dist/locale/en.json';
import {ValidationProvider, ValidationObserver} from 'vee-validate';
import i18n from "../../shared/i18n";

Vue.component('ValidationProvider', ValidationProvider);
Vue.component('ValidationObserver', ValidationObserver);


extend('min_date', {
  message(name, rule) {
    return i18n.t("Select date after {date}", {date: rule[0]})
  },
  validate(value, args) {
    if (!args || args.length === 0) {
      return true
    }
    let minDate = new Date(args[0])
    let valueDate = new Date(value)
    return !(valueDate < minDate)
  }
});

extend('max_date', {
  message(name, rule) {
    return i18n.t("Select date up to {date}", {date: rule[0]})
  },
  validate(value, args) {
    if (!args || args.length === 0) {
      return true
    }
    let maxDate = new Date(args[0])
    let valueDate = new Date(value)
    return !(valueDate > maxDate)
  }
});

/** Empty is allowed; otherwise numeric and >= 0 (e.g. report hammer price filters). */
extend('optional_non_negative', {
  message: () => i18n.t('Value must be zero or greater'),
  validate(value) {
    if (value === null || value === '' || value === undefined) {
      return true;
    }
    const n = Number(value);
    if (Number.isNaN(n)) {
      return false;
    }
    return n >= 0;
  },
});

/** Max hammer price must be >= min; cross-field: report_hammer_max_gte_min:@report_gen_hammer_price_min */
extend('report_hammer_max_gte_min', {
  params: ['target'],
  message: () => i18n.t('Maximum must be greater than or equal to minimum'),
  validate(value, {target}) {
    if (value === null || value === '' || value === undefined) {
      return true;
    }
    if (target === null || target === '' || target === undefined) {
      return true;
    }
    const nMax = Number(value);
    const nMin = Number(target);
    if (Number.isNaN(nMax) || Number.isNaN(nMin)) {
      return true;
    }
    return nMax >= nMin;
  },
});

/** Min hammer price must be <= max; cross-field: report_hammer_min_lte_max:@report_gen_hammer_price_max */
extend('report_hammer_min_lte_max', {
  params: ['target'],
  message: () => i18n.t('Minimum must be less than or equal to maximum'),
  validate(value, {target}) {
    if (value === null || value === '' || value === undefined) {
      return true;
    }
    if (target === null || target === '' || target === undefined) {
      return true;
    }
    const nMin = Number(value);
    const nMax = Number(target);
    if (Number.isNaN(nMin) || Number.isNaN(nMax)) {
      return true;
    }
    return nMin <= nMax;
  },
});

extend('decimal', {
  params: ['decimals'],
  message: (name, rule) => i18n.t('Value must have no more than {decimals} decimal places', { decimals: rule.decimals }),
  validate(value, { decimals }) {
    if (value === null || value === '' || value === undefined) {
      return true;
    }
    return new RegExp(`^\\d+(\\.\\d{1,${decimals}})?$`).test(String(value));
  },
});

let customRuleMessage = {
  required: i18n.t("This field is required"),
  numeric: i18n.t("This field may only contain numeric characters"),
  email: i18n.t("The email field must be a valid email"),
  min: function (name, rule) {
    return i18n.t('The field must contain at least {val} characters', {val: rule.length})
  },
  max: function (name, rule) {
    return i18n.t('Following field cannot exceed {val} characters', {val: rule.length})
  },
  max_value: function (name, rule) {
    return i18n.t('Value must be {val} or less', {val: rule.max})
  },
  min_value: function (name, rule) {
    return i18n.t('Value must be {val} or more', {val: rule.min})
  },
  integer: function () {
    return i18n.t('The field must be an integer')
  },
  confirmed: function (name) {
    if (name.includes('password')) {
      return i18n.t("Passwords do not match")
    }
    return i18n.t('The field confirmation does not match');
  },
  regex: function (name, rule) {
    if (rule.regex.source === AT_LEAST_ONE_UPPER_CASE_REGEX) {
      return i18n.t("The field must contain at least one uppercase character");
    } else if (rule.regex.source === PHONE_NUMBER_REGEX) {
      return i18n.t("Please enter correct phone number");
    } else if (rule.regex.source === NO_EMPTY_SPACE_REGEX) {
      return i18n.t("Field can not contain empty space");
    } else if (rule.regex.source === ALPHA_NUMERIC_REGEX) {
      return i18n.t("Field should only contain alphanumeric characters");
    } else if (rule.regex.source === NO_EMPTY_SPACE_AND_FORWARD_SLASH) {
      return i18n.t("Field can not contain empty space and forward slash");
    } else if (rule.regex.source === LOT_NO_REGEX) {
      return i18n.t("Please enter the correct lot number, at least one number and max three uppercase letters");
    } else if (rule.regex.source === EMAIL_REGEX) {
      return i18n.t("The email field must be a valid email");
    } else if (rule.regex.source === PASSWORD_REGEX) {
      return i18n.t("Password must meet the following requirements: minimum 12 characters, at least one upper case English letter, at least one lower case English letter, at least one number and at least one of the following special characters: #?!@$ %^&*-.");
    } else if (rule.regex.source === VALID_FILE_NAME_REGEX) {
      return i18n.t("Please enter valid file name.");
    }
    return i18n.t('The field format is invalid');
  }
}

for (let ruleName in customRuleMessage) {
  messages[ruleName] = customRuleMessage[ruleName];
}

Object.keys(rules).forEach(rule => {
  extend(rule, {
    ...rules[rule], // copies rule configuration
    message: messages[rule]
  });
});

export const PHONE_NUMBER_REGEX = '^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\\s\\./0-9]*$';
export const AT_LEAST_ONE_UPPER_CASE_REGEX = '(?=.*[A-Z])';
export const PASSWORD_REGEX = '^(?=.*[A-Z])(?=.*[a-z])(?=.*\\d)(?=.*[#?!@$ %^&*\\-\\.])[A-Za-z\\d#?!@$ %^&*\\-\\.]{12,30}$';
export const NO_EMPTY_SPACE_REGEX = '^[\\S]+$';
export const ALPHA_NUMERIC_REGEX = '^[a-zA-Z0-9]*$';
export const NO_EMPTY_SPACE_AND_FORWARD_SLASH = '^[^\\s*\\/]+$';
export const LOT_NO_REGEX = '^(?!0)[0-9]{1,8}[A-Z]{0,3}$';
export const EMAIL_REGEX = '^\\s*[\\w-\\.+]+@([\\w-]+\\.)+[\\w-]{2,20}\\s*$';
export const VALID_FILE_NAME_REGEX = '^[\\sa-zA-Z0-9_-]+$';

export function getPhoneRuleObj(isRequired = false) {
  return {
    regex: PHONE_NUMBER_REGEX,
    max: 30,
    required: isRequired
  }
}

export function getPasswordRegex(isRequired = false, extraRules = {}) {
  return {
    regex: PASSWORD_REGEX,
    max: 30,
    min: 12,
    required: isRequired,
    ...extraRules
  }
}

export function getFilenameRuleObj(isRequired = false) {
  return {
    regex: VALID_FILE_NAME_REGEX,
    max: 60,
    required: isRequired
  }
}
